"use client";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaPinterest,
  FaYoutube,
  FaLinkedinIn,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";

interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  social?: string;
}

function RenderSocialIcon({ social }: { social: string | undefined }) {
  const iconClass = "text-lg hover:text-blue-400 transition-colors duration-300";

  switch (social) {
    case "TWITTER":
      return <FaTwitter className={iconClass} />;
    case "INSTAGRAM":
      return <FaInstagram className={iconClass} />;
    case "PINTEREST":
      return <FaPinterest className={iconClass} />;
    case "FACEBOOK":
      return <FaFacebookF className={iconClass} />;
    case "YOUTUBE":
      return <FaYoutube className={iconClass} />;
    case "LINKEDIN":
      return <FaLinkedinIn className={iconClass} />;
    default:
      return null;
  }
}

export function Footer_Layout4({
  logoUrl,
  logoText,
  menuLinks,
  legalLinks,
  socialLinks,
  contactEmail,
  contactPhone,
  newsletterDescription = "Join our newsletter to receive updates and exclusive offers.",
}: {
  logoUrl: string | null;
  logoText: string | null;
  menuLinks: Array<FooterLink>;
  legalLinks: Array<FooterLink>;
  socialLinks: Array<FooterLink>;
  newsletterDescription?: string;
  contactEmail?: string;
  contactPhone?: string;
}) {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="container mx-auto px-5">
        <div className="text-center mb-10">
          {logoUrl ? (
            <img src={logoUrl} alt={logoText || ""} className="h-12 mx-auto mb-6" />
          ) : (
            <h3 className="text-3xl font-bold mb-6">{logoText}</h3>
          )}
          
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            {newsletterDescription}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-sm font-medium transition-colors duration-300 whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          {/* Quick links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              {menuLinks.slice(0, 5).map((link) => (
                <li key={link.id}>
                  <a
                    href={link.url}
                    className="text-gray-400 hover:text-white text-sm transition-colors"
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <div className="space-y-4">
              {contactEmail && (
                <div className="flex items-center gap-3 text-gray-400">
                  <FaEnvelope className="text-blue-400" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-white transition-colors">
                    {contactEmail}
                  </a>
                </div>
              )}
              {contactPhone && (
                <div className="flex items-center gap-3 text-gray-400">
                  <FaPhone className="text-blue-400" />
                  <a href={`tel:${contactPhone}`} className="hover:text-white transition-colors">
                    {contactPhone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Social links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
            <div className="flex gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target={link.newTab ? "_blank" : "_self"}
                  className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors duration-300"
                  aria-label={link.social}
                >
                  <RenderSocialIcon social={link.social} />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright and legal */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-800">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} {logoText}. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            {legalLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                {link.text}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}